import { useAddNoteTopic, useGetTopics } from '@/hooks/api/dashboard'
import useAppStore from '@/store/useAppStore'
import { EgoObject, Topic } from '@/types'
import {
  Box,
  Button,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { shallow } from 'zustand/shallow'
import { LoadingButton } from '@mui/lab'
import { CheckBox, Input, NoResults, RModal, TagPrivacy, TagTopic } from '@/components'
import { useNavigate } from 'react-router-dom'
import colors from '@/styles/colors'
import { ChevronLeft, Close, DeleteOutline, CheckCircle } from '@mui/icons-material'
import { SelectTopicContainer } from './__styled'
import { PrivacyTypes, TopicType, TopicTypes } from '@/utils/constants'
import { toast } from 'react-hot-toast'
import { useBrowserQuery } from '@/hooks/common'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import { CBox, LoadingButtonRounded, RBox } from '../__styled'
import { convertToGMT, getPlainNoteTextFromNote } from '@/utils/helper'
import ReactTimeago from 'react-timeago'
import { IMAGES } from '@/assets/images'

interface Props {
  selectedEgoObjects: EgoObject[]
  onClickBack: () => void
  showClose?: boolean
  onClickDelete: () => void
}

export default function SelectTopics({
  selectedEgoObjects,
  onClickBack,
  showClose = false,
  onClickDelete,
}: Props) {
  const [page, setPage] = useState(1)
  const [endReached, setEndReached] = useState(false)
  const [selectedTopicIDs, setSelectedTopicIDs] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [searchText, setSearchText] = useState('')

  const [topicList] = useAppStore((state) => [state.home.topicList], shallow)
  const getTopics = useGetTopics()
  const addNoteToTopic = useAddNoteTopic()
  const navigate = useNavigate()
  const query = useBrowserQuery()
  const containerID = query.get('container')

  useEffect(() => {
    setEndReached(false)
    getTopics.mutate({
      pageNo: 1,
      interaction: 3,
      topicType: TopicType.ALBUM,
    })
  }, [])

  useEffect(() => {
    if (getTopics.isSuccess && getTopics.data && !getTopics.isError) {
      if (getTopics.data.length < 20) {
        setEndReached(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTopics.isSuccess, getTopics.data, getTopics.isError])

  useEffect(() => {
    setSelectedTopicIDs([])
  }, [containerID])

  const onChangeSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
  }
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
    }
  }

  const topics = useMemo(() => {
    const list = Object.values(topicList)
    // sort by date updated
    list.sort((a, b) => {
      if (a.dateUpdated > b.dateUpdated) return -1
      if (a.dateUpdated < b.dateUpdated) return 1
      return 0
    })
    return list
  }, [topicList])

  const filteredTopics = useMemo(() => {
    if (searchText.trim().length > 0 && topics.length > 0) {
      return topics.filter((e) => e.topicName.toLowerCase().includes(searchText.toLowerCase()))
    } else {
      return topics
    }
  }, [topics, searchText])

  if (selectedEgoObjects.length === 0) return <SelectTopicContainer />

  const onClickNew = () => {
    navigate(`/topic/create`)
  }

  if (getTopics.isSuccess && getTopics.data.length == 0 && page === 1) {
    return (
      <Box p={5} display='flex' justifyContent={'center'}>
        <NoResults interaction={0} topicType={2} onClick={onClickNew} />
      </Box>
    )
  }

  const onClickShowMore = () => {
    if (endReached || getTopics.isLoading || !topics.length) {
      return
    }
    getTopics.mutate({
      pageNo: page + 1,
      interaction: 3,
      topicType: TopicType.ALBUM,
    })
    setPage(page + 1)
  }

  const onToggleTopic = (topicID: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      selectedTopicIDs.push(topicID)
    } else {
      const index = selectedTopicIDs.findIndex((i) => i === topicID)
      selectedTopicIDs.splice(index, 1)
    }
    setSelectedTopicIDs([...selectedTopicIDs])
  }

  const onClickAdd = async () => {
    if (selectedEgoObjects.length > 0 && selectedTopicIDs.length > 0) {
      setIsSubmitting(true)

      const egoItems = selectedEgoObjects.map((e, i) => ({
        sequenceID: `${i}`,
        egoID: e.egoID,
        photoFullURL: e.egoFileURL,
        photoAlbumURL: e.egoAlbumURL,
      }))

      try {
        // Make API calls sequentially to avoid race conditions
        for (const topicID of selectedTopicIDs) {
          await new Promise((resolve, reject) => {
            addNoteToTopic.mutate(
              {
                topicID: topicID,
                egoItems: egoItems,
                noteName: 'untitled',
                noteText: 'placeholder',
              },
              {
                onSuccess: () => {
                  resolve(true)
                },
                onError: (error) => {
                  console.error('Error adding note to topic:', error)
                  reject(error)
                },
              },
            )
          })
        }

        toast.success('Image has been added successfully', { position: 'bottom-right' })
        setSelectedTopicIDs([])
      } catch (error) {
        console.error('Error adding images to albums:', error)
        toast.error('Failed to add images to albums', { position: 'bottom-right' })
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px 16px 0 0',
        overflow: 'hidden',
        boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.12)',
      }}
    >
      {/* Stripe-level Header */}
      <Box
        sx={{
          padding: '28px 28px 20px',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          background: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
        }}
      >
        <Box display='flex' justifyContent='space-between' alignItems='flex-start' mb={2}>
          <Box>
            <Typography
              variant='h5'
              sx={{
                fontSize: '22px',
                fontWeight: 700,
                color: '#0f172a',
                letterSpacing: '-0.02em',
                marginBottom: '6px',
                lineHeight: 1.2,
              }}
            >
              Add to Albums
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                borderRadius: '8px',
                backgroundColor: 'rgba(99, 102, 241, 0.08)',
                border: '1px solid rgba(99, 102, 241, 0.15)',
              }}
            >
              <Typography
                variant='body2'
                sx={{
                  color: '#6366f1',
                  fontSize: '13px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                }}
              >
                {selectedEgoObjects.length > 0 ? (
                  <>
                    {selectedEgoObjects.length} photo{selectedEgoObjects.length === 1 ? '' : 's'}{' '}
                    selected
                  </>
                ) : (
                  'No photos selected'
                )}
              </Typography>
            </Box>
          </Box>

          {showClose && (
            <IconButton
              onClick={onClickBack}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: 'rgba(248, 250, 252, 0.8)',
                border: '1px solid rgba(226, 232, 240, 0.6)',
                borderRadius: '12px',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: '#ffffff',
                  borderColor: '#cbd5e1',
                  transform: 'rotate(90deg)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                },
                '&:active': {
                  transform: 'rotate(90deg) scale(0.95)',
                },
              }}
            >
              <SVG src={SVGS.CloseIcon} width='18' height='18' color='#475569' />
            </IconButton>
          )}
        </Box>

        {/* Stripe-level Search Input */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: '#ffffff',
                borderColor: '#cbd5e1',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              },
              '&:focus-within': {
                backgroundColor: '#ffffff',
                border: '2px solid #6366f1',
                boxShadow: '0 0 0 4px rgba(99, 102, 241, 0.1)',
              },
            }}
          >
            <InputBase
              value={searchText}
              placeholder='Search your albums...'
              onChange={onChangeSearchText}
              onKeyDown={handleKeyDown}
              name='search'
              startAdornment={
                <Box sx={{ display: 'flex', alignItems: 'center', marginRight: '10px' }}>
                  <SVG src={SVGS.SearchIcon} width='18' height='18' color='#94a3b8' />
                </Box>
              }
              sx={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#0f172a',
                letterSpacing: '-0.01em',
                '& input': {
                  padding: '0',
                  '&::placeholder': {
                    color: '#94a3b8',
                    opacity: 1,
                    fontSize: '14px',
                    fontWeight: 500,
                  },
                },
              }}
            />

            {/* Clear Search Button */}
            {searchText && (
              <IconButton
                onClick={() => setSearchText('')}
                sx={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '28px',
                  height: '28px',
                  backgroundColor: '#e2e8f0',
                  color: '#64748b',
                  borderRadius: '50%',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: '#6366f1',
                    color: '#ffffff',
                    transform: 'translateY(-50%) rotate(90deg) scale(1.05)',
                  },
                  '&:active': {
                    transform: 'translateY(-50%) rotate(90deg) scale(0.95)',
                  },
                }}
              >
                <SVG src={SVGS.CloseIcon} width='14' height='14' />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
      {getTopics.isLoading && page === 1 && (
        <Box mt={3} display='flex' justifyContent={'center'}>
          <CircularProgress
            size={40}
            thickness={4}
            sx={{
              color: '#6366f1',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
        </Box>
      )}

      {/* Album List */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          overflowY: 'auto',
          padding: '8px 0',
        }}
      >
        {filteredTopics.map((item: Topic) => {
          const topicData = TopicTypes[Number(item.topicType)]
          const privacyData = PrivacyTypes[Number(item.visibility)]
          if (!topicData || !privacyData) return null
          const imageURL =
            item.latestNote && item.latestNote.egoList.length > 0
              ? item.latestNote.egoList[item.latestNote.egoList.length - 1].photoAlbumURL
              : IMAGES.topicPreviewPlaceholder
          const isSelected = selectedTopicIDs.includes(item.topicID)

          return (
            <Box
              key={item.topicID}
              sx={{
                padding: '14px 24px',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                borderLeft: isSelected ? '4px solid #6366f1' : '4px solid transparent',
                backgroundColor: isSelected ? 'rgba(99, 102, 241, 0.05)' : 'transparent',
                '&:hover': {
                  backgroundColor: isSelected ? 'rgba(99, 102, 241, 0.08)' : '#f8fafc',
                },
              }}
              onClick={() => {
                const event = {
                  target: { checked: !isSelected },
                } as React.ChangeEvent<HTMLInputElement>
                onToggleTopic(item.topicID)(event)
              }}
            >
              <Box display='flex' alignItems='center' gap={3}>
                {/* Album Thumbnail */}
                <Box
                  sx={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    backgroundColor: '#f8fafc',
                    backgroundImage: `url(${imageURL})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    flexShrink: 0,
                    border: isSelected ? '3px solid #6366f1' : '2px solid rgba(226, 232, 240, 0.8)',
                    boxShadow: isSelected
                      ? '0 4px 12px rgba(99, 102, 241, 0.2)'
                      : '0 1px 3px rgba(15, 23, 42, 0.08)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />

                {/* Album Info */}
                <Box flex={1} sx={{ minWidth: 0 }}>
                  <Typography
                    variant='h6'
                    sx={{
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#0f172a',
                      letterSpacing: '-0.01em',
                      marginBottom: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.3,
                    }}
                  >
                    {getPlainNoteTextFromNote(item.firstNote)}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      fontSize: '13px',
                      color: '#64748b',
                      marginBottom: '2px',
                      fontWeight: 600,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {item.noteCount} photos
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      fontSize: '12px',
                      color: '#94a3b8',
                      fontWeight: 500,
                    }}
                  >
                    Added <ReactTimeago date={convertToGMT(item.dateCreated)} />
                  </Typography>
                </Box>

                {/* Stripe-level Checkbox */}
                <Box
                  sx={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '6px',
                    border: isSelected ? 'none' : '2px solid #cbd5e1',
                    background: isSelected
                      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                      : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    flexShrink: 0,
                    boxShadow: isSelected ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none',
                  }}
                >
                  {isSelected && <CheckCircle sx={{ color: '#ffffff', fontSize: '16px' }} />}
                </Box>
              </Box>
            </Box>
          )
        })}
      </Box>

      {(page > 1 || !getTopics.isLoading) && !endReached && (
        <Box m={3} display='flex' justifyContent={'center'}>
          <LoadingButton
            variant='contained'
            onClick={onClickShowMore}
            loading={getTopics.isLoading && page > 1}
            disabled={endReached}
            sx={{
              background:
                getTopics.isLoading && page > 1
                  ? 'rgba(248, 250, 252, 0.8)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              color: '#475569',
              padding: '10px 28px',
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              letterSpacing: '-0.01em',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background:
                  getTopics.isLoading && page > 1
                    ? 'rgba(248, 250, 252, 0.8)'
                    : 'linear-gradient(135deg, #ffffff 0%, #ffffff 100%)',
                borderColor: '#cbd5e1',
                transform: getTopics.isLoading && page > 1 ? 'none' : 'translateY(-2px)',
                boxShadow:
                  getTopics.isLoading && page > 1
                    ? '0 2px 8px rgba(0, 0, 0, 0.04)'
                    : '0 4px 16px rgba(0, 0, 0, 0.08)',
              },
              '&:active': {
                transform: getTopics.isLoading && page > 1 ? 'none' : 'translateY(0)',
              },
              '&:disabled': {
                background: 'rgba(248, 250, 252, 0.8)',
                color: '#cbd5e1',
              },
            }}
          >
            {endReached ? '' : 'Load More Albums'}
          </LoadingButton>
        </Box>
      )}
      {/* Stripe-level Footer */}
      {topics.length > 0 && (
        <Box
          sx={{
            padding: '20px 24px',
            borderTop: '1px solid rgba(226, 232, 240, 0.8)',
            background: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {/* Delete Button */}
          <IconButton
            onClick={onClickDelete}
            disabled={!selectedEgoObjects.length}
            sx={{
              width: 48,
              height: 48,
              background: selectedEgoObjects.length
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : 'rgba(248, 250, 252, 0.8)',
              color: selectedEgoObjects.length ? '#ffffff' : '#cbd5e1',
              border: '1px solid',
              borderColor: selectedEgoObjects.length ? 'transparent' : 'rgba(226, 232, 240, 0.8)',
              borderRadius: '12px',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: selectedEgoObjects.length
                  ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                  : 'rgba(248, 250, 252, 0.8)',
                transform: selectedEgoObjects.length ? 'translateY(-2px)' : 'none',
                boxShadow: selectedEgoObjects.length ? '0 4px 12px rgba(239, 68, 68, 0.3)' : 'none',
              },
              '&:active': {
                transform: selectedEgoObjects.length ? 'translateY(0)' : 'none',
              },
              '&:disabled': {
                background: 'rgba(248, 250, 252, 0.8)',
                color: '#cbd5e1',
                cursor: 'not-allowed',
              },
            }}
          >
            <SVG src={SVGS.TrashIcon} width='20' height='20' />
          </IconButton>

          {/* Add to Albums Button */}
          <LoadingButton
            variant='contained'
            onClick={onClickAdd}
            loading={isSubmitting}
            disabled={!selectedEgoObjects.length || !selectedTopicIDs.length}
            sx={{
              flex: 1,
              height: 48,
              borderRadius: '12px',
              background:
                selectedEgoObjects.length && selectedTopicIDs.length
                  ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                  : 'rgba(248, 250, 252, 0.8)',
              color: '#ffffff',
              fontSize: '15px',
              fontWeight: 700,
              letterSpacing: '-0.01em',
              textTransform: 'none',
              border: '1px solid',
              borderColor:
                selectedEgoObjects.length && selectedTopicIDs.length
                  ? 'transparent'
                  : 'rgba(226, 232, 240, 0.8)',
              boxShadow:
                selectedEgoObjects.length && selectedTopicIDs.length
                  ? '0 2px 8px rgba(99, 102, 241, 0.25)'
                  : 'none',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background:
                  selectedEgoObjects.length && selectedTopicIDs.length
                    ? 'linear-gradient(135deg, #5558e3 0%, #7c3aed 100%)'
                    : 'rgba(248, 250, 252, 0.8)',
                boxShadow:
                  selectedEgoObjects.length && selectedTopicIDs.length
                    ? '0 4px 16px rgba(99, 102, 241, 0.35)'
                    : 'none',
                transform:
                  selectedEgoObjects.length && selectedTopicIDs.length
                    ? 'translateY(-2px)'
                    : 'none',
              },
              '&:active': {
                transform:
                  selectedEgoObjects.length && selectedTopicIDs.length ? 'translateY(0)' : 'none',
              },
              '&:disabled': {
                background: 'rgba(248, 250, 252, 0.8)',
                color: '#cbd5e1',
                transform: 'none',
                boxShadow: 'none',
                cursor: 'not-allowed',
              },
              '& .MuiLoadingButton-loadingIndicator': {
                color: '#ffffff',
              },
            }}
          >
            {selectedTopicIDs.length > 0
              ? `Add to ${selectedTopicIDs.length} Album${selectedTopicIDs.length > 1 ? 's' : ''}`
              : 'Select albums to add photos'}
          </LoadingButton>
        </Box>
      )}
    </Box>
  )
}
